
import React, { useState } from 'react';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import { ChevronDown, ChevronUp, Star, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ThreoAIPricingFeatureTable = () => {
  const [isTableVisible, setIsTableVisible] = useState(false);
  
  return (
    <section className="py-12 md:py-16 bg-synthreo-black">
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-center font-gilroy flex items-center justify-center">
            <Star size={24} className="mr-2 text-synthreo-blue-vibrant" />
            Feature Table
            <Star size={24} className="ml-2 text-synthreo-blue-vibrant" />
          </h2>
          
          <Button
            variant="ghost"
            className="mt-4 flex items-center justify-center text-synthreo-blue-vibrant hover:text-synthreo-blue-vibrant hover:bg-synthreo-black-light/30"
            onClick={() => setIsTableVisible(!isTableVisible)}
          >
            {isTableVisible ? (
              <>Hide Feature Table <ChevronUp className="ml-2 h-4 w-4" /></>
            ) : (
              <>Show Feature Table <ChevronDown className="ml-2 h-4 w-4" /></>
            )}
          </Button>
        </div>
        
        {isTableVisible && (
          <div className="overflow-x-auto rounded-lg border border-gray-800 shadow-lg">
            <div className="w-full">
              <Table className="w-full border-collapse">
                <TableHeader>
                  <TableRow className="bg-synthreo-black-light border-b border-gray-800">
                    <TableHead className="py-5 px-4 text-center font-medium text-synthreo-blue-vibrant" style={{width: '20%'}}>Feature</TableHead>
                    <TableHead className="py-5 px-4 text-center font-medium text-synthreo-blue-vibrant" style={{width: '26.66%'}}>
                      <div className="text-center">
                        Team Tier<br/>
                        <span className="text-xs font-normal italic text-gray-400">(Ideal for small teams)</span><br/>
                        <span className="text-xs font-normal text-gray-300">$30/user/month</span>
                      </div>
                    </TableHead>
                    <TableHead className="py-5 px-4 text-center font-medium text-synthreo-blue-vibrant" style={{width: '26.66%'}}>
                      <div className="text-center">
                        Business Tier<br/>
                        <span className="text-xs font-normal italic text-gray-400">(Growing companies with automation needs)</span><br/>
                        <span className="text-xs font-normal text-gray-300">$40/user/month</span>
                      </div>
                    </TableHead>
                    <TableHead className="py-5 px-4 text-center font-medium text-synthreo-blue-vibrant" style={{width: '26.66%'}}>
                      <div className="text-center">
                        Enterprise Tier<br/>
                        <span className="text-xs font-normal italic text-gray-400">(Custom plans for large teams or advanced security needs)</span>
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* All features in a single list */}
                  <TableRow className="border-b border-gray-800">
                    <TableCell className="py-4 px-4 text-sm font-medium" style={{width: '20%'}}>LLM Access</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>
                      Choose 1 LLM from model picker (e.g., GPT-4, Claude, Gemini)<br/>
                      <span className="text-xs text-gray-400">via secure Azure-hosted infrastructure</span>
                    </TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>
                      Choose up to 4 LLMs from model picker<br/>
                      <span className="text-xs text-gray-400">flexible per workflow or user</span>
                    </TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>
                      Unlimited LLM selection, including bring-your-own model support
                    </TableCell>
                  </TableRow>
                  
                  <TableRow className="border-b border-gray-800">
                    <TableCell className="py-4 px-4 text-sm font-medium" style={{width: '20%'}}>Usage</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Unlimited<sup>*</sup></TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Unlimited<sup>*</sup></TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Unlimited<sup>*</sup></TableCell>
                  </TableRow>
                  
                  <TableRow className="border-b border-gray-800">
                    <TableCell className="py-4 px-4 text-sm font-medium" style={{width: '20%'}}>Web Search</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>
                      200 real-time queries/month<br/>
                      <span className="text-xs text-gray-400">(always up-to-date answers)</span>
                    </TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Unlimited real-time web search</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Custom search configuration & usage controls</TableCell>
                  </TableRow>
                  
                  <TableRow className="border-b border-gray-800">
                    <TableCell className="py-4 px-4 text-sm font-medium" style={{width: '20%'}}>Memory / Knowledge</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>
                      User-specific memory (RAG)<br/>
                      works across LLMs
                    </TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>
                      Enhanced user + team-wide memory<br/>
                      company data accessible to all users
                    </TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>
                      Full organizational knowledge graph<br/>
                      custom memory policies
                    </TableCell>
                  </TableRow>
                  
                  <TableRow className="border-b border-gray-800">
                    <TableCell className="py-4 px-4 text-sm font-medium" style={{width: '20%'}}>Document Upload & Analysis</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>
                      Up to 10MB per file<br/>
                      <span className="text-xs text-gray-400">(PDF, DOCX, XLSX, etc.)</span>
                    </TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>
                      Up to 25MB per file<br/>
                      Multiple file uploads
                    </TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Custom file size limits & formats</TableCell>
                  </TableRow>
                  
                  <TableRow className="border-b border-gray-800">
                    <TableCell className="py-4 px-4 text-sm font-medium" style={{width: '20%'}}>Custom AI Instructions</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Standard system prompt customization</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>
                      Advanced prompt tuning<br/>
                      Multi-role instruction sets
                    </TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Fully managed persona programming & response control</TableCell>
                  </TableRow>
                  
                  <TableRow className="border-b border-gray-800">
                    <TableCell className="py-4 px-4 text-sm font-medium" style={{width: '20%'}}>Support</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>
                      Email support<br/>
                      <span className="text-xs text-gray-400">(24-48hr SLA)</span>
                    </TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>
                      Priority support<br/>
                      <span className="text-xs text-gray-400">(chat + onboarding call)</span>
                    </TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Dedicated account manager + SLA-backed response</TableCell>
                  </TableRow>
                  
                  <TableRow className="border-b border-gray-800">
                    <TableCell className="py-4 px-4 text-sm font-medium" style={{width: '20%'}}>Conversation History</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>30 days per user</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>90 days per user</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Custom retention policies</TableCell>
                  </TableRow>
                  
                  <TableRow className="border-b border-gray-800">
                    <TableCell className="py-4 px-4 text-sm font-medium" style={{width: '20%'}}>Analytics Dashboard</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>
                      Basic usage metrics<br/>
                      <span className="text-xs text-gray-400">(chats/user, activity logs)</span>
                    </TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>
                      Advanced team-level insights<br/>
                      <span className="text-xs text-gray-400">(trends, use case reporting)</span>
                    </TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Custom reporting + API access for BI tools</TableCell>
                  </TableRow>
                  
                  <TableRow className="border-b border-gray-800">
                    <TableCell className="py-4 px-4 text-sm font-medium" style={{width: '20%'}}>Mobile & Desktop Access</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}><Check size={18} className="mx-auto text-synthreo-green-neon" /></TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}><Check size={18} className="mx-auto text-synthreo-green-neon" /></TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}><Check size={18} className="mx-auto text-synthreo-green-neon" /></TableCell>
                  </TableRow>
                  
                  <TableRow className="border-b border-gray-800">
                    <TableCell className="py-4 px-4 text-sm font-medium" style={{width: '20%'}}>Custom Conversation Starters</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>5 per user</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>15 per user</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Unlimited with organization templates</TableCell>
                  </TableRow>
                  
                  <TableRow className="border-b border-gray-800">
                    <TableCell className="py-4 px-4 text-sm font-medium" style={{width: '20%'}}>Conversation Templates</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>–</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Included</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Fully customizable with branding</TableCell>
                  </TableRow>
                  
                  <TableRow className="border-b border-gray-800">
                    <TableCell className="py-4 px-4 text-sm font-medium" style={{width: '20%'}}>Security & Privacy</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>
                      Azure-hosted with ZDR<br/>
                      <span className="text-xs text-gray-400">(Zero Data Retention)</span>
                    </TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>
                      Azure-hosted with ZDR<br/>
                      SSO available
                    </TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Enterprise security: SSO, SCIM, region locking, policy management</TableCell>
                  </TableRow>
                  
                  <TableRow className="border-b border-gray-800">
                    <TableCell className="py-4 px-4 text-sm font-medium" style={{width: '20%'}}>Compliance</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Inherits SOC 2, HIPAA, GDPR via Azure infrastructure</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Same as Team, with optional SSO setup</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Same as above, with support for custom DPAs and data residency controls</TableCell>
                  </TableRow>
                  
                  <TableRow className="border-b border-gray-800">
                    <TableCell className="py-4 px-4 text-sm font-medium" style={{width: '20%'}}>AI Agent Embedding</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Optional add-on</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Optional add-on</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Included or tailored per tenant</TableCell>
                  </TableRow>
                  
                  <TableRow className="border-b border-gray-800">
                    <TableCell className="py-4 px-4 text-sm font-medium" style={{width: '20%'}}>No-Code Agent Builder Access</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>–</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>
                      Powered by Synthreo backend<br/>
                      <span className="text-xs text-gray-400">(Threo team implements for you)</span>
                    </TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Full access to build/manage internal workflows via Synthreo</TableCell>
                  </TableRow>
                  
                  <TableRow className="border-b border-gray-800">
                    <TableCell className="py-4 px-4 text-sm font-medium" style={{width: '20%'}}>API Access</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>–</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Add-on available</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Full API access for integrations</TableCell>
                  </TableRow>
                  
                  <TableRow className="border-b border-gray-800">
                    <TableCell className="py-4 px-4 text-sm font-medium" style={{width: '20%'}}>Custom Branding & Environment</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>
                      Branding included on all plans<br/>
                      <span className="text-xs text-gray-400">(logo, colors, prompts)</span>
                    </TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Branding included</TableCell>
                    <TableCell className="py-4 px-4 text-sm text-center" style={{width: '26.66%'}}>Branding + custom URLs + user segmentation</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>
        )}
        
        {isTableVisible && (
          <div className="mt-10 max-w-4xl mx-auto">
            <div className="bg-synthreo-black-light/30 p-6 rounded-lg border border-gray-800 text-center">
              <h3 className="text-lg font-bold mb-2 inline-flex items-center justify-center">
                <span className="text-synthreo-blue-vibrant mr-2">*</span> Unlimited Usage Disclaimer
              </h3>
              <p className="text-sm text-gray-300 mt-2">
                Unlimited usage applies under ThreoAI's fair use policy and is designed to comfortably support daily 
                use by SMB and mid-sized teams. For high-volume automation or programmatic workloads, 
                custom throughput options are available.
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ThreoAIPricingFeatureTable;
